import { TaskActionQuestion } from '@/components/work/TaskActionQuestion';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowRight, Loader2, RotateCcw, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { LightViewAction } from '@shared/types/views';
import type { AccountTaskActionResponse } from '@shared/types/task-actions';
import { Button } from '@/components/ui/Button';
import { ViewRouter } from '@/components/views/ViewRouter';
import { toSafeUiMessage } from '@/lib/dashboard-errors';
import { executeAccountTaskAction } from '@/lib/task-action';

type InlineCapabilityAction = Extract<LightViewAction, { type: 'inline_capability' }>;

export const ACCOUNT_TASK_COMPLETED_EVENT = 'domino:account-task-completed';

export const AccountCapabilityLauncher = ({
  action,
  onClose,
}: {
  action: InlineCapabilityAction | null;
  onClose: () => void;
}) => {
  const navigate = useNavigate();
  const launchedRef = useRef<string | null>(null);
  const [result, setResult] = useState<AccountTaskActionResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [launching, setLaunching] = useState(false);

  const launch = useCallback(async (clarificationOverride?: { key: string; value: string }) => {
    if (!action?.sourceRunId || !action.sourceActionLabel) return;
    setLaunching(true);
    setError(null);
    setResult(null);
    try {
      const response = await executeAccountTaskAction({
        sourceRunId: action.sourceRunId,
        sourceActionId: action.sourceActionId ?? null,
        sourceActionIndex: action.sourceActionIndex ?? null,
        sourceActionLabel: action.sourceActionLabel,
        ...(clarificationOverride
          ? { clarification: clarificationOverride }
          : {}),
      });
      setResult(response);
      if (response.status === 'completed') {
        window.dispatchEvent(new CustomEvent(ACCOUNT_TASK_COMPLETED_EVENT, {
          detail: { taskRunId: response.taskRunId, sourceRunId: action.sourceRunId },
        }));
      }
    } catch (launchError) {
      setError(toSafeUiMessage(
        launchError instanceof Error ? launchError.message : null,
        'Domino n’a pas pu préparer cette action.'
      ));
    } finally {
      setLaunching(false);
    }
  }, [action]);

  useEffect(() => {
    if (!action) {
      launchedRef.current = null;
      return;
    }
    if (!action.sourceRunId || !action.sourceActionLabel) return;
    const launchKey = `${action.sourceRunId}:${action.sourceActionId ?? action.sourceActionLabel}`;
    if (launchedRef.current === launchKey) return;
    launchedRef.current = launchKey;
    setResult(null);
    setError(null);
    void launch();
  }, [action, launch]);

  if (!action) return null;
  const actionTitle = action.sourceActionLabel?.trim() || action.label;

  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center bg-domino-ink/30 p-0 backdrop-blur-sm sm:items-center sm:p-4">
      <section
        role="dialog"
        aria-modal="true"
        aria-label={actionTitle}
        className="max-h-[94vh] w-full max-w-3xl overflow-y-auto rounded-t-[24px] border border-domino-cardBorder bg-domino-white shadow-2xl sm:max-h-[90vh] sm:rounded-[24px]"
      >
        <header className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-domino-cardBorder bg-domino-white/95 px-5 py-4 backdrop-blur sm:px-6">
          <h2 className="line-clamp-2 text-lg font-semibold text-domino-ink sm:text-xl">
            {actionTitle}
          </h2>
          <Button size="icon" variant="ghost" aria-label="Fermer" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </header>

        <div className="p-5 sm:p-6">
          {launching && (
            <div className="flex min-h-44 flex-col items-center justify-center text-center">
              <Loader2 className="h-7 w-7 animate-spin text-domino-coral" />
              <p className="mt-4 text-sm font-semibold text-domino-ink">Préparation…</p>
              <p className="mt-1 text-sm text-domino-inkSoft">
                Le résultat sera conservé avec ce compte.
              </p>
            </div>
          )}

          {!launching && error && (
            <div className="space-y-4">
              <p className="text-sm text-domino-inkSoft">{error}</p>
              <Button variant="secondary" onClick={() => void launch()}>
                <RotateCcw className="mr-2 h-4 w-4" />
                Réessayer
              </Button>
            </div>
          )}

          {!launching && result?.status === 'needs_input' && <TaskActionQuestion question={result.question} onAnswer={answer => void launch(answer)} />}

          {!launching && result?.status === 'running' && (
            <div className="rounded-2xl border border-blue-200 bg-blue-50 px-5 py-4 text-sm text-blue-800">
              <p className="font-semibold">Brouillon déjà en préparation</p>
              <p className="mt-1 leading-6">{result.message}</p>
            </div>
          )}

          {!launching && (result?.status === 'unavailable' || result?.status === 'failed') && (
            <div className="text-sm text-domino-inkSoft">
              <p>{toSafeUiMessage(
                result.message,
                result.status === 'failed'
                  ? 'Domino n’a pas pu préparer cette action.'
                  : 'Cette action n’est pas disponible pour le moment.'
              )}</p>
              {result.status === 'failed' && (
                <div className="mt-4">
                  <Button variant="secondary" onClick={() => void launch()}>
                    <RotateCcw className="mr-2 h-4 w-4" />
                    Réessayer
                  </Button>
                </div>
              )}
            </div>
          )}

          {!launching && result?.status === 'completed' && (
            <div className="space-y-4">
              <p className="text-sm leading-6 text-domino-inkSoft">{result.message}</p>
              <ViewRouter payload={result.viewEnvelope} />
              {result.artifactLocation.kind === 'inline_thread' && <div className="flex flex-wrap items-center justify-between gap-3 border-t border-domino-cardBorder pt-4">
                <p className="text-sm text-domino-inkSoft">
                  Disponible dans {result.artifactLocation.subjectLabel}.
                </p>
                <Button
                  variant="secondary"
                  onClick={() => {
                    const location = result.artifactLocation;
                    if (location.kind !== 'inline_thread') return;
                    const query = new URLSearchParams({
                      kind: location.subjectKind,
                      label: location.subjectLabel,
                      result: location.taskActionRunId,
                    });
                    if (location.subjectKey) query.set('key', location.subjectKey);
                    onClose();
                    navigate(
                      `/threads/${encodeURIComponent(location.subjectId)}?${query.toString()}`
                    );
                  }}
                >
                  Ouvrir le compte
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};
